import React, { useEffect, useMemo, useState } from "react";

import { ActionMenu } from "@remar/shared/dist/components/ActionMenu";
import { IColumn } from "@remar/shared/dist/components/MaterialTable";
import { ColumnHeader, StyledCellText } from "@remar/shared/dist/components/Table/styles";
import { EmptyState, TableWrapper, Wrapper } from "@remar/shared/dist/layouts";
import { BannerNotification, NotificationTypeId } from "@remar/shared/dist/models";
import { useConfirm } from "material-ui-confirm";
import { useDispatch } from "react-redux";
import { getFullState as getFullAuthState } from "store/features/Auth/auth.slice";
import { fetchBanners, selectBannersFullState, updateBanner } from "store/features/Banners";
import { useAppSelector } from "store/hooks";

import EditBusinessBanners from "./EditBusinesssBanners";
import { Marker } from "./components";
import { mappedAudienceTypes } from "./utils";

const BusinessBannersList = () => {
	const dispatch = useDispatch();
	const confirm = useConfirm();
	const [searchText, setSearchText] = useState("");
	const [editBanner, setEditBanner] = useState<BannerNotification | null>(null);
	const { isLoading, banners, totalItems } = useAppSelector(selectBannersFullState);
	const {
		accessPerRoute: { canEdit }
	} = useAppSelector(getFullAuthState);

	useEffect(() => {
		dispatch(
			fetchBanners({
				page: 1,
				perPage: 10,
				filters: {
					notificationTypeId: [NotificationTypeId.SystemNotification]
				}
			})
		);
	}, [dispatch]);

	const handleUpdateBannerStatus = (id: number, status: boolean) => {
		confirm({
			title: "Business Banner",
			description: `Are you sure you want to ${status ? "Deactivate" : "Activate"} Banner?`,
			cancellationButtonProps: { variant: "contained", color: "secondary" },
			confirmationButtonProps: { variant: "contained" },
			confirmationText: `${status ? "Deactivate" : "Activate"}`
		}).then(() => {
			dispatch(
				updateBanner({
					id,
					data: { isActive: !status },
					cb: () => {
						dispatch(
							fetchBanners({
								page: 1,
								perPage: 10,
								filters: {
									notificationTypeId: [NotificationTypeId.SystemNotification]
								}
							})
						);
					}
				})
			);
		});
	};

	const tableColumns: Array<IColumn<BannerNotification>> = useMemo(
		() => [
			{
				alignment: "left",
				label: <ColumnHeader>Name</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { body } }) => <StyledCellText>{body}</StyledCellText>,
				dataKey: "name"
			},
			{
				alignment: "left",
				label: <ColumnHeader>Status</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { isActive } }) => (
					<StyledCellText>
						<Marker status={isActive ? "active" : "deactivated"} /> {isActive ? "Active" : "Deactivated"}
					</StyledCellText>
				),
				dataKey: "status"
			},
			{
				alignment: "left",
				label: <ColumnHeader>Trigger</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { title } }) => <StyledCellText>{title}</StyledCellText>,
				dataKey: "trigger"
			},
			{
				alignment: "left",
				label: <ColumnHeader>Coupon</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { coupon } }) => <StyledCellText>{coupon?.name}</StyledCellText>,
				dataKey: "coupon"
			},
			{
				alignment: "left",
				label: <ColumnHeader>Audience</ColumnHeader>,
				width: 200,
				Cell: ({
					rowData: {
						notificationAudienceType: { id }
					}
				}) => <StyledCellText>{mappedAudienceTypes[id]}</StyledCellText>,
				dataKey: "audience"
			},
			{
				alignment: "left",
				width: 90,
				Cell: ({ rowData }) => (
					<ActionMenu
						customMenuItems={[
							{
								label: "Edit",
								onClick: () => {
									setEditBanner(rowData);
								},
								disabled: !canEdit,
								visible: true
							},
							{
								label: `${rowData.isActive ? "Deactivate" : "Activate"}`,
								onClick: () => handleUpdateBannerStatus(rowData.id, rowData.isActive),
								disabled: !canEdit,
								visible: true
							}
						]}
					/>
				),
				dataKey: "menu"
			}
		],
		[canEdit]
	);

	const handleSearchBarChange = (searchText: string) => {
		return dispatch(
			fetchBanners({
				searchKeyword: searchText,
				filters: {
					notificationTypeId: [NotificationTypeId.SystemNotification]
				}
			})
		);
	};

	return (
		<Wrapper heading="Business Banners">
			<TableWrapper
				tableTitle="Business Banners"
				data={banners}
				emptyState={<EmptyState description="No banners added" />}
				isLoading={isLoading}
				onChangePage={() => {}}
				page={1}
				perPage={10}
				tableColumns={tableColumns}
				totalItems={totalItems}
				searchText={searchText}
				setSearchText={setSearchText}
				handleSearchBarChange={handleSearchBarChange}
			/>
			{editBanner && (
				<EditBusinessBanners
					setEditBanner={setEditBanner}
					editBanner={editBanner}
					onClose={() => setEditBanner(null)}
				/>
			)}
		</Wrapper>
	);
};

export default BusinessBannersList;
